import React, { useState } from 'react';

// Traps:
// 1. "Full Name" field is pre-filled with wrong value — must be cleared and retyped
// 2. "Role" dropdown defaults to "Manager" but task says "Engineer"
// 3. "Access Level" looks disabled (greyed label) but is NOT — must be set to "Level 3"
// 4. "Send Welcome Email" checkbox is pre-checked — task says do NOT check it
// 5. "Start Date" has a placeholder that LOOKS like a value but field is empty — must fill
// 6. "Department" is a text input (not dropdown) — exact spelling matters: "Platform Engineering"
// 7. "Employee ID" has a note "(auto-generated)" next to it but MUST be manually entered as "EMP-20250312"

const EXPECTED = {
  fullName: 'Jordan Blake',
  employeeId: 'EMP-20250312',
  role: 'Engineer',
  department: 'Platform Engineering',
  accessLevel: 'Level 3',
  startDate: '2026-06-01',
  sendWelcomeEmail: false,
  reportingManager: 'Sarah Chen',
};

function Field({ label, hint, children, error }) {
  return (
    <div className="ab-field">
      <label className="ab-label">{label}{hint && <span className="ab-field-hint"> {hint}</span>}</label>
      {children}
      {error && <span className="ab-field-err">{error}</span>}
    </div>
  );
}

export default function C17_TrapForm({ onComplete }) {
  const [form, setForm] = useState({
    fullName: 'Alex Johnson',
    employeeId: '',
    role: 'Manager',
    department: '',
    accessLevel: 'Level 1',
    startDate: '',
    sendWelcomeEmail: true,
    reportingManager: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [interactions, setInteractions] = useState(0);
  const [errorAttempts, setErrorAttempts] = useState(0);

  const ch = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setErrors(er => ({ ...er, [name]: '' }));
    setInteractions(n => n + 1);
  };

  const validate = () => {
    const errs = {};
    if (!form.fullName.trim()) errs.fullName = 'Required';
    if (!form.employeeId.trim()) errs.employeeId = 'Required';
    if (!form.department.trim()) errs.department = 'Required';
    if (!form.startDate) errs.startDate = 'Required';
    if (!form.reportingManager.trim()) errs.reportingManager = 'Required';
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); setErrorAttempts(n => n + 1); return; }
    setSubmitted(true);
    const norm = s => String(s).trim().toLowerCase();
    const results = {
      fullName: { expected: EXPECTED.fullName, got: form.fullName.trim(), pass: norm(form.fullName) === norm(EXPECTED.fullName) },
      employeeId: { expected: EXPECTED.employeeId, got: form.employeeId.trim(), pass: form.employeeId.trim() === EXPECTED.employeeId },
      role: { expected: EXPECTED.role, got: form.role, pass: form.role === EXPECTED.role },
      department: { expected: EXPECTED.department, got: form.department.trim(), pass: norm(form.department) === norm(EXPECTED.department) },
      accessLevel: { expected: EXPECTED.accessLevel, got: form.accessLevel, pass: form.accessLevel === EXPECTED.accessLevel },
      startDate: { expected: EXPECTED.startDate, got: form.startDate, pass: form.startDate === EXPECTED.startDate },
      sendWelcomeEmail: { expected: 'false', got: String(form.sendWelcomeEmail), pass: form.sendWelcomeEmail === false },
      reportingManager: { expected: EXPECTED.reportingManager, got: form.reportingManager.trim(), pass: norm(form.reportingManager) === norm(EXPECTED.reportingManager) },
    };
    const correct = Object.values(results).filter(r => r.pass).length;
    onComplete({
      score: Math.round((correct / 8) * 100),
      details: results,
      passed: correct === 8,
      interactionCount: interactions,
      errorAttempts,
    });
  };

  return (
    <div className="ab-challenge-ui">
      <div className="ab-trap-banner">⚠️ Some fields have pre-filled or misleading values. Read the task instructions carefully.</div>

      <div className="ab-form-grid-2">
        <Field label="Full Name" error={errors.fullName}>
          <input name="fullName" value={form.fullName} onChange={ch}
            className={`ab-input${errors.fullName ? ' ab-input-err' : ''}`} disabled={submitted} />
        </Field>

        <Field label="Employee ID" hint="(enter manually)" error={errors.employeeId}>
          <input name="employeeId" value={form.employeeId} onChange={ch}
            className={`ab-input${errors.employeeId ? ' ab-input-err' : ''}`}
            placeholder="EMP-XXXXXXXX" disabled={submitted} />
        </Field>

        <Field label="Role">
          <select name="role" value={form.role} onChange={ch} className="ab-input" disabled={submitted}>
            {['Manager', 'Engineer', 'Designer', 'Analyst', 'Director'].map(v => <option key={v}>{v}</option>)}
          </select>
        </Field>

        <Field label="Department" error={errors.department}>
          <input name="department" value={form.department} onChange={ch}
            className={`ab-input${errors.department ? ' ab-input-err' : ''}`}
            placeholder="Type department name exactly" disabled={submitted} />
        </Field>

        <Field label={<span style={{ color: 'var(--muted)' }}>Access Level <span style={{ fontSize: 10 }}>(system default)</span></span>}>
          <select name="accessLevel" value={form.accessLevel} onChange={ch} className="ab-input" disabled={submitted}>
            {['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Admin'].map(v => <option key={v}>{v}</option>)}
          </select>
        </Field>

        <Field label="Start Date" error={errors.startDate}>
          <input name="startDate" type="date" value={form.startDate} onChange={ch}
            className={`ab-input${errors.startDate ? ' ab-input-err' : ''}`} disabled={submitted} />
        </Field>

        <Field label="Reporting Manager" error={errors.reportingManager}>
          <input name="reportingManager" value={form.reportingManager} onChange={ch}
            className={`ab-input${errors.reportingManager ? ' ab-input-err' : ''}`}
            placeholder="Manager full name" disabled={submitted} />
        </Field>

        <Field label="Notifications">
          <label className="ab-chk-label">
            <input type="checkbox" name="sendWelcomeEmail" checked={form.sendWelcomeEmail} onChange={ch} disabled={submitted} />
            Send Welcome Email to new employee
          </label>
        </Field>
      </div>

      {!submitted
        ? <button className="ab-action-btn" onClick={handleSubmit}>Create Employee</button>
        : <div className="ab-submitted-badge">✓ Submitted — Evaluating...</div>
      }
    </div>
  );
}
